import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Search, FileText, Monitor, ClipboardCheck, X, Filter,
    Calendar, BookOpen, GraduationCap, Loader2, FolderOpen,
    ChevronDown, ChevronRight, RotateCcw, Trash2, ArchiveRestore, AlertTriangle,
    CheckSquare, Square, Minus
} from 'lucide-react';
import api from '../services/api';

/**
 * SavedLessonsLibrary — Browse, reopen, and manage saved lessons.
 *
 * Features:
 * - Search by title
 * - Filter by subject, grade, date
 * - Artifact chips showing what was generated
 * - Click to reopen canonical PostGenerationView workspace
 * - Delete with confirmation (moves to recycle bin)
 * - Recycle bin with restore, permanent delete, and "Empty Bin"
 * - Bulk selection for batch delete/restore
 * - Improved topic-first titles
 */
const SavedLessonsLibrary = ({
    isOpen,
    onClose,
    userProfile,
    onOpenLesson,
}) => {
    // Data state
    const [lessons, setLessons] = useState([]);
    const [trashLessons, setTrashLessons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Tab state
    const [activeTab, setActiveTab] = useState('lessons');

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [subjectFilter, setSubjectFilter] = useState('');
    const [gradeFilter, setGradeFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Collapsed subject groups
    const [collapsedGroups, setCollapsedGroups] = useState({});

    // Delete confirmation state
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // ── Bulk Selection State ──
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [bulkRestoring, setBulkRestoring] = useState(false);

    // ── Empty Recycle Bin ──
    const [emptyingBin, setEmptyingBin] = useState(false);
    const [showEmptyBinConfirm, setShowEmptyBinConfirm] = useState(false);

    // Fetch lessons on open
    useEffect(() => {
        if (isOpen) {
            fetchLessons();
            fetchTrash();
            // Reset selection when opening
            setSelectedIds(new Set());
            setBulkMode(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Reset selection when switching tabs
    useEffect(() => {
        setSelectedIds(new Set());
        setBulkMode(false);
    }, [activeTab]);

    const fetchLessons = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/api/documents/list', {
                params: { teacher_id: userProfile?.email, doc_type: 'lesson' }
            });
            setLessons(response.data.documents || []);
        } catch (err) {
            console.error('Error fetching lessons:', err);
            setError('Failed to load lessons');
        } finally {
            setLoading(false);
        }
    };

    const fetchTrash = async () => {
        try {
            const response = await api.get('/api/documents/trash', {
                params: { teacher_id: userProfile?.email }
            });
            setTrashLessons(response.data.documents || []);
        } catch (err) {
            console.error('Error fetching trash:', err);
        }
    };

    // Single delete (move to trash)
    const handleDelete = async (lesson) => {
        setDeleting(true);
        try {
            await api.delete(`/api/documents/${lesson.id}`, {
                params: { teacher_id: userProfile?.email }
            });
            await fetchLessons();
            await fetchTrash();
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Error deleting lesson:', err);
            setError('Failed to delete lesson');
        } finally {
            setDeleting(false);
        }
    };

    // Single restore
    const handleRestore = async (lesson) => {
        try {
            await api.post(`/api/documents/${lesson.id}/restore`, null, {
                params: { teacher_id: userProfile?.email }
            });
            await fetchLessons();
            await fetchTrash();
        } catch (err) {
            console.error('Error restoring lesson:', err);
            setError('Failed to restore lesson');
        }
    };

    // Permanent delete
    const handlePermanentDelete = async (lesson) => {
        setDeleting(true);
        try {
            await api.delete(`/api/documents/${lesson.id}/permanent`, {
                params: { teacher_id: userProfile?.email }
            });
            await fetchTrash();
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Error permanently deleting:', err);
            setError('Failed to permanently delete');
        } finally {
            setDeleting(false);
        }
    };

    // ── Bulk Operations ──
    const toggleSelection = useCallback((id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const selectAll = useCallback(() => {
        const ids = activeTab === 'lessons'
            ? filteredLessons.map(l => l.id)
            : trashLessons.map(l => l.id);
        setSelectedIds(new Set(ids));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, trashLessons]);

    const deselectAll = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setBulkDeleting(true);
        try {
            const promises = Array.from(selectedIds).map(id =>
                api.delete(`/api/documents/${id}`, { params: { teacher_id: userProfile?.email } })
            );
            await Promise.allSettled(promises);
            await fetchLessons();
            await fetchTrash();
            setSelectedIds(new Set());
            setBulkMode(false);
        } catch (err) {
            console.error('Bulk delete error:', err);
            setError('Some lessons could not be deleted');
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleBulkRestore = async () => {
        if (selectedIds.size === 0) return;
        setBulkRestoring(true);
        try {
            const promises = Array.from(selectedIds).map(id =>
                api.post(`/api/documents/${id}/restore`, null, { params: { teacher_id: userProfile?.email } })
            );
            await Promise.allSettled(promises);
            await fetchLessons();
            await fetchTrash();
            setSelectedIds(new Set());
            setBulkMode(false);
        } catch (err) {
            console.error('Bulk restore error:', err);
            setError('Some lessons could not be restored');
        } finally {
            setBulkRestoring(false);
        }
    };

    const handleBulkPermanentDelete = async () => {
        if (selectedIds.size === 0) return;
        setBulkDeleting(true);
        try {
            const promises = Array.from(selectedIds).map(id =>
                api.delete(`/api/documents/${id}/permanent`, { params: { teacher_id: userProfile?.email } })
            );
            await Promise.allSettled(promises);
            await fetchTrash();
            setSelectedIds(new Set());
            setBulkMode(false);
        } catch (err) {
            console.error('Bulk permanent delete error:', err);
            setError('Some lessons could not be permanently deleted');
        } finally {
            setBulkDeleting(false);
        }
    };

    // ── Empty Recycle Bin ──
    const handleEmptyBin = async () => {
        setEmptyingBin(true);
        try {
            const promises = trashLessons.map(l =>
                api.delete(`/api/documents/${l.id}/permanent`, { params: { teacher_id: userProfile?.email } })
            );
            await Promise.allSettled(promises);
            await fetchTrash();
            setShowEmptyBinConfirm(false);
        } catch (err) {
            console.error('Empty bin error:', err);
            setError('Failed to empty recycle bin');
        } finally {
            setEmptyingBin(false);
        }
    };

    // Get unique filter values
    const subjects = useMemo(() => {
        const set = new Set(lessons.map(l => l.metadata?.subject).filter(Boolean));
        return Array.from(set).sort();
    }, [lessons]);

    const grades = useMemo(() => {
        const set = new Set(lessons.map(l => l.metadata?.grade_level).filter(Boolean));
        return Array.from(set).sort();
    }, [lessons]);

    // Filter lessons
    const filteredLessons = useMemo(() => {
        return lessons.filter(lesson => {
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const title = (lesson.title || '').toLowerCase();
                const content = (lesson.content || '').toLowerCase();
                if (!title.includes(query) && !content.includes(query)) return false;
            }
            if (subjectFilter && lesson.metadata?.subject !== subjectFilter) return false;
            if (gradeFilter && lesson.metadata?.grade_level !== gradeFilter) return false;
            return true;
        });
    }, [lessons, searchQuery, subjectFilter, gradeFilter]);

    const handleOpenLesson = (lesson) => {
        let lessonData = null;
        try {
            if (lesson.content) lessonData = JSON.parse(lesson.content);
        } catch { /* Content is raw text */ }

        if (!lessonData && lesson.metadata?.lesson_data) {
            lessonData = lesson.metadata.lesson_data;
        }

        if (lessonData && lesson.metadata?.generation_receipt && !lessonData.generation_receipt) {
            lessonData = {
                ...lessonData,
                generation_receipt: lesson.metadata.generation_receipt,
            };
        }

        if (!lessonData && !lesson.content) {
            setError('This lesson is missing its content. Please regenerate.');
            return;
        }

        onOpenLesson({
            id: lesson.id,
            title: lesson.title,
            text: lesson.content,
            data: lessonData,
            metadata: lesson.metadata,
            lesson_id: lesson.metadata?.lesson_id,
            success: true
        });
        onClose();
    };


    const clearFilters = () => {
        setSearchQuery('');
        setSubjectFilter('');
        setGradeFilter('');
    };

    const hasActiveFilters = searchQuery || subjectFilter || gradeFilter;

    // Group filtered lessons by subject
    const groupedLessons = useMemo(() => {
        const groups = {};
        filteredLessons.forEach(lesson => {
            const subject = lesson.metadata?.subject || 'Uncategorized';
            if (!groups[subject]) groups[subject] = [];
            groups[subject].push(lesson);
        });
        return Object.entries(groups)
            .sort(([a], [b]) => {
                if (a === 'Uncategorized') return 1;
                if (b === 'Uncategorized') return -1;
                return a.localeCompare(b);
            })
            .map(([subject, lessons]) => ({ subject, lessons }));
    }, [filteredLessons]);

    const toggleGroup = (subject) => {
        setCollapsedGroups(prev => ({ ...prev, [subject]: !prev[subject] }));
    };

    if (!isOpen) return null;

    const currentItems = activeTab === 'lessons' ? filteredLessons : trashLessons;
    const allSelected = currentItems.length > 0 && currentItems.every(l => selectedIds.has(l.id));
    const _someSelected = currentItems.some(l => selectedIds.has(l.id));

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-black/5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                            <FolderOpen className="text-white" size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-[#1d1d1f]">My Saved Lessons</h2>
                            <p className="text-xs text-[#999]">{lessons.length} lessons saved</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Bulk mode toggle */}
                        <button
                            onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                bulkMode
                                    ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                    : 'bg-[#f5f5f7] text-[#666] hover:bg-[#eee]'
                            }`}
                        >
                            {bulkMode ? 'Done' : 'Select'}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-[#f5f5f7] rounded-full transition-colors"
                        >
                            <X size={20} className="text-[#999]" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-black/5">
                    <button
                        onClick={() => setActiveTab('lessons')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                            activeTab === 'lessons'
                                ? 'text-amber-600 border-b-2 border-amber-500 bg-amber-50/30'
                                : 'text-[#999] hover:text-[#666] hover:bg-[#fafafa]'
                        }`}
                    >
                        <FolderOpen size={14} className="inline mr-2" />
                        My Lessons ({lessons.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('trash')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                            activeTab === 'trash'
                                ? 'text-red-500 border-b-2 border-red-400 bg-red-50/30'
                                : 'text-[#999] hover:text-[#666] hover:bg-[#fafafa]'
                        }`}
                    >
                        <Trash2 size={14} className="inline mr-2" />
                        Recycle Bin ({trashLessons.length})
                    </button>
                </div>

                {/* Bulk Action Bar */}
                {bulkMode && selectedIds.size > 0 && (
                    <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={allSelected ? deselectAll : selectAll}
                                className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                            >
                                {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                            <span className="text-xs text-amber-600">{selectedIds.size} selected</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {activeTab === 'lessons' ? (
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleting}
                                    className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                >
                                    {bulkDeleting && <Loader2 size={12} className="animate-spin" />}
                                    <Trash2 size={12} />
                                    Delete Selected
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={handleBulkRestore}
                                        disabled={bulkRestoring}
                                        className="px-3 py-1.5 text-xs font-medium bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                        {bulkRestoring && <Loader2 size={12} className="animate-spin" />}
                                        <ArchiveRestore size={12} />
                                        Restore Selected
                                    </button>
                                    <button
                                        onClick={handleBulkPermanentDelete}
                                        disabled={bulkDeleting}
                                        className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                        {bulkDeleting && <Loader2 size={12} className="animate-spin" />}
                                        <Trash2 size={12} />
                                        Delete Forever
                                    </button>
                                </>
                            )}
                            <button
                                onClick={deselectAll}
                                className="px-2 py-1.5 text-xs text-amber-600 hover:text-amber-800"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Search & Filters (lessons tab only) */}
                {activeTab === 'lessons' && (
                    <div className="p-4 border-b border-black/5 bg-[#fafafa] space-y-3">
                        <div className="flex gap-3">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#bbb]" size={16} />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search lessons..."
                                    className="w-full pl-10 pr-4 py-2 bg-white border border-black/5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
                                />
                            </div>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-3 py-2 flex items-center gap-2 rounded-xl border text-xs font-medium transition-colors ${
                                    showFilters || hasActiveFilters
                                        ? 'bg-amber-50 border-amber-200 text-amber-700'
                                        : 'bg-white border-black/5 text-[#666] hover:bg-[#f5f5f7]'
                                }`}
                            >
                                <Filter size={14} />
                                Filters
                                {hasActiveFilters && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />}
                            </button>
                            <button
                                onClick={fetchLessons}
                                className="px-3 py-2 flex items-center rounded-xl border border-black/5 bg-white text-[#666] hover:bg-[#f5f5f7] transition-colors"
                                title="Refresh"
                            >
                                <RotateCcw size={14} className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>

                        {showFilters && (
                            <div className="flex gap-3 items-center">
                                <select
                                    value={subjectFilter}
                                    onChange={(e) => setSubjectFilter(e.target.value)}
                                    className="px-3 py-2 bg-white border border-black/5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
                                >
                                    <option value="">All Subjects</option>
                                    {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <select
                                    value={gradeFilter}
                                    onChange={(e) => setGradeFilter(e.target.value)}
                                    className="px-3 py-2 bg-white border border-black/5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
                                >
                                    <option value="">All Grades</option>
                                    {grades.map(g => <option key={g} value={g}>Grade {g}</option>)}
                                </select>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters} className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1">
                                        <X size={12} /> Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Trash banner */}
                {activeTab === 'trash' && trashLessons.length > 0 && (
                    <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-amber-700">
                            <AlertTriangle size={14} />
                            Items are permanently deleted after 30 days.
                        </div>
                        <button
                            onClick={() => setShowEmptyBinConfirm(true)}
                            className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                        >
                            Empty Recycle Bin
                        </button>
                    </div>
                )}

                {/* Lesson List */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="animate-spin text-amber-500" size={28} />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-red-500 mb-4">{error}</p>
                            <button
                                onClick={() => { fetchLessons(); fetchTrash(); setError(null); }}
                                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                            >
                                Retry
                            </button>
                        </div>
                    ) : activeTab === 'lessons' ? (
                        filteredLessons.length === 0 ? (
                            <div className="text-center py-12">
                                <FolderOpen className="mx-auto text-[#ccc] mb-4" size={48} />
                                <p className="text-[#999]">
                                    {hasActiveFilters ? 'No lessons match your filters' : 'No saved lessons yet'}
                                </p>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters} className="mt-3 text-amber-600 hover:text-amber-800 text-sm">
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {groupedLessons.map(({ subject, lessons: groupLessons }) => (
                                    <div key={subject}>
                                        <button
                                            onClick={() => toggleGroup(subject)}
                                            className="w-full flex items-center gap-2 px-3 py-2 bg-[#f5f5f7] hover:bg-[#eee] rounded-lg transition-colors mb-2"
                                        >
                                            {collapsedGroups[subject]
                                                ? <ChevronRight size={14} className="text-[#999]" />
                                                : <ChevronDown size={14} className="text-[#999]" />
                                            }
                                            <BookOpen size={14} className="text-amber-500" />
                                            <span className="font-semibold text-[#444] text-sm">{subject}</span>
                                            <span className="text-xs text-[#bbb] ml-auto">
                                                {groupLessons.length} lesson{groupLessons.length !== 1 ? 's' : ''}
                                            </span>
                                        </button>
                                        {!collapsedGroups[subject] && (
                                            <div className="space-y-2 ml-2">
                                                {groupLessons.map(lesson => (
                                                    <LessonCard
                                                        key={lesson.id}
                                                        lesson={lesson}
                                                        onClick={() => handleOpenLesson(lesson)}
                                                        onDelete={() => setDeleteConfirm({ lesson, permanent: false })}
                                                        bulkMode={bulkMode}
                                                        selected={selectedIds.has(lesson.id)}
                                                        onToggleSelect={() => toggleSelection(lesson.id)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        trashLessons.length === 0 ? (
                            <div className="text-center py-12">
                                <Trash2 className="mx-auto text-[#ccc] mb-4" size={48} />
                                <p className="text-[#999]">Recycle bin is empty</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {trashLessons.map(lesson => (
                                    <TrashCard
                                        key={lesson.id}
                                        lesson={lesson}
                                        onRestore={() => handleRestore(lesson)}
                                        onDelete={() => setDeleteConfirm({ lesson, permanent: true })}
                                        bulkMode={bulkMode}
                                        selected={selectedIds.has(lesson.id)}
                                        onToggleSelect={() => toggleSelection(lesson.id)}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-black/5 bg-[#fafafa] text-center">
                    <p className="text-xs text-[#999]">
                        {activeTab === 'lessons'
                            ? `Showing ${filteredLessons.length} of ${lessons.length} lessons`
                            : `${trashLessons.length} items in recycle bin`
                        }
                    </p>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <Trash2 className="text-red-600" size={20} />
                            </div>
                            <h3 className="text-lg font-bold text-[#1d1d1f]">
                                {deleteConfirm.permanent ? 'Permanently Delete?' : 'Delete Lesson?'}
                            </h3>
                        </div>
                        <p className="text-[#444] mb-2">
                            <strong>"{deleteConfirm.lesson.title}"</strong>
                        </p>
                        <p className="text-sm text-[#999] mb-6">
                            {deleteConfirm.permanent
                                ? 'This action cannot be undone.'
                                : 'This lesson will be moved to the recycle bin. You can restore it within 30 days.'}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleting}
                                className="flex-1 px-4 py-2 border border-black/10 text-[#666] rounded-xl hover:bg-[#fafafa] transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => deleteConfirm.permanent
                                    ? handlePermanentDelete(deleteConfirm.lesson)
                                    : handleDelete(deleteConfirm.lesson)
                                }
                                disabled={deleting}
                                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {deleting && <Loader2 size={16} className="animate-spin" />}
                                {deleteConfirm.permanent ? 'Delete Forever' : 'Move to Trash'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty Recycle Bin Confirmation */}
            {showEmptyBinConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <Trash2 className="text-red-600" size={20} />
                            </div>
                            <h3 className="text-lg font-bold text-[#1d1d1f]">Empty Recycle Bin?</h3>
                        </div>
                        <p className="text-sm text-[#666] mb-6">
                            This will permanently delete <strong>{trashLessons.length}</strong> lesson{trashLessons.length !== 1 ? 's' : ''}. This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowEmptyBinConfirm(false)}
                                disabled={emptyingBin}
                                className="flex-1 px-4 py-2 border border-black/10 text-[#666] rounded-xl hover:bg-[#fafafa] transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEmptyBin}
                                disabled={emptyingBin}
                                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {emptyingBin && <Loader2 size={16} className="animate-spin" />}
                                Empty Bin
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Derive a readable display title from lesson data.
 * Prefers topic-based title over generic "Lesson Plan - Math" format.
 */
const getDisplayTitle = (lesson) => {
    const meta = lesson.metadata || {};
    const raw = lesson.title || '';

    // If lesson has topic metadata, prefer that
    if (meta.topic && meta.topic.length > 3) {
        const date = lesson.created_at ? new Date(lesson.created_at) : null;
        const dateLabel = date
            ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '';
        return dateLabel ? `${meta.topic} · ${dateLabel}` : meta.topic;
    }

    // If title is generic, try to improve it
    const isGeneric = /^Lesson Plan[\s\-–—]*/i.test(raw);
    if (isGeneric) {
        const subject = meta.subject || '';
        const date = lesson.created_at ? new Date(lesson.created_at) : null;
        const dateLabel = date
            ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '';
        if (subject && dateLabel) return `${subject} · ${dateLabel}`;
        if (subject) return subject;
    }

    return raw || 'Untitled Lesson';
};

/**
 * Individual lesson card component
 */
const LessonCard = ({ lesson, onClick, onDelete, bulkMode, selected, onToggleSelect }) => {
    const displayTitle = getDisplayTitle(lesson);
    const { metadata, created_at } = lesson;

    const date = created_at ? new Date(created_at) : null;
    const dateStr = date ? date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
    }) : '';

    const hasSlides = metadata?.has_slides;
    const hasPacket = metadata?.has_packet;
    const hasQuiz = metadata?.has_quiz;

    return (
        <div className="w-full p-4 bg-white border border-black/5 rounded-xl hover:border-amber-300 hover:shadow-sm transition-all group">
            <div className="flex items-start gap-3">
                {/* Bulk checkbox */}
                {bulkMode && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
                        className="mt-1 flex-shrink-0"
                    >
                        {selected
                            ? <CheckSquare size={18} className="text-amber-500" />
                            : <Square size={18} className="text-[#ccc] hover:text-amber-400" />
                        }
                    </button>
                )}

                {/* Icon */}
                <button
                    onClick={onClick}
                    className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 hover:bg-amber-100 transition-colors"
                >
                    <BookOpen className="text-amber-600" size={18} />
                </button>

                {/* Content */}
                <button onClick={onClick} className="flex-1 min-w-0 text-left">
                    <h3 className="font-semibold text-[#1d1d1f] text-sm truncate group-hover:text-amber-700 transition-colors">
                        {displayTitle}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[#999]">
                        {metadata?.subject && (
                            <span className="flex items-center gap-1">
                                <BookOpen size={11} />
                                {metadata.subject}
                            </span>
                        )}
                        {metadata?.grade_level && (
                            <span className="flex items-center gap-1">
                                <GraduationCap size={11} />
                                Grade {metadata.grade_level}
                            </span>
                        )}
                        {dateStr && (
                            <span className="flex items-center gap-1">
                                <Calendar size={11} />
                                {dateStr}
                            </span>
                        )}
                    </div>

                    {/* Artifact chips */}
                    <div className="flex items-center gap-1.5 mt-2">
                        {hasSlides && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full flex items-center gap-1">
                                <Monitor size={10} /> Slides
                            </span>
                        )}
                        {hasPacket && (
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-medium rounded-full flex items-center gap-1">
                                <FileText size={10} /> Packet
                            </span>
                        )}
                        {hasQuiz && (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded-full flex items-center gap-1">
                                <ClipboardCheck size={10} /> Quiz
                            </span>
                        )}
                    </div>
                </button>

                {/* Delete button (not in bulk mode) */}
                {!bulkMode && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="p-1.5 text-[#ccc] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete lesson"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Trash item card component
 */
const TrashCard = ({ lesson, onRestore, onDelete, bulkMode, selected, onToggleSelect }) => {
    const { title, metadata, days_remaining } = lesson;

    return (
        <div className="w-full p-4 bg-[#fafafa] border border-black/5 rounded-xl">
            <div className="flex items-start gap-3">
                {bulkMode && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
                        className="mt-1 flex-shrink-0"
                    >
                        {selected
                            ? <CheckSquare size={18} className="text-amber-500" />
                            : <Square size={18} className="text-[#ccc] hover:text-amber-400" />
                        }
                    </button>
                )}

                <div className="w-10 h-10 bg-[#eee] rounded-lg flex items-center justify-center flex-shrink-0">
                    <BookOpen className="text-[#bbb]" size={18} />
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#666] text-sm truncate">{title}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[#bbb]">
                        {metadata?.subject && <span>{metadata.subject}</span>}
                        <span className="text-red-400 font-medium">{days_remaining} days left</span>
                    </div>
                </div>

                {!bulkMode && (
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={onRestore}
                            className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1"
                        >
                            <ArchiveRestore size={12} /> Restore
                        </button>
                        <button
                            onClick={onDelete}
                            className="p-1.5 text-[#bbb] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete forever"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SavedLessonsLibrary;
